import { SurveyStatus, UserProfileRole } from '@@types/api.types';
import { MainLayout } from '@components/layouts/main.layout';
import { CompanyDetailsTile } from '@components/pages/company/components/company-info-card.component';
import { HelmetWrapper } from '@components/shared/helmet.component';
import { PageviewTracker } from '@components/shared/pageview-tracker.component';
import { ReferralTile } from '@components/shared/referral-tile.component';
import { TechnologiesTrlSpectrumTile } from '@components/shared/technologies-trl-spectrum-tile.component';
import { useInnovatorGetSurveys } from '@queries/survey.query.ts';
import { useTechnologiesQueryOptions } from '@queries/technology.query';
import { useGetCurrentUser, useGetUserSelectedCompanyId } from '@queries/users.query';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HomeOnboardingTile } from './components/home-onboarding-tile.component';
import { HomeTechnologyDraftSurveys } from './components/home-technology-surveys.component';
import { TechnologiesGridTile } from './components/technologies-tile.component';

export const HomePage: React.FC = () => {
  const technologiesQueryOptions = useTechnologiesQueryOptions();
  const { data: technologies = [] } = useQuery(technologiesQueryOptions);
  const { data: user } = useGetCurrentUser();
  const selectedCompanyId = useGetUserSelectedCompanyId();
  const navigate = useNavigate();

  const isInnovator = user?.profile?.role === UserProfileRole.INNOVATOR;
  const isExpert = user?.profile?.role === UserProfileRole.EXPERT;
  const isIntel = user?.profile?.role === UserProfileRole.INTELLIGENCE;
  const isAdmin = user?.profile?.role === UserProfileRole.ADMIN;
  const userHasValidAccess = Boolean(user?.hasValidAccess);
  const hasCompany = (user?.companies?.length ?? 0) > 0;

  const { data: draftSurveys = [] } = useInnovatorGetSurveys({ status: SurveyStatus.DRAFT });

  const company = user?.companies?.find((c) => c.id === selectedCompanyId) || user?.companies?.[0];

  useEffect(() => {
    if (!userHasValidAccess) return;

    if (isExpert && user?.expertProfile) {
      navigate('/assessments');
    }
    if (isIntel) {
      navigate('/tech-challenges');
    }
    if (isAdmin) {
      navigate('/admin/users');
    }
  }, [isExpert, isIntel, isAdmin, navigate, user?.expertProfile, userHasValidAccess]);

  return (
    <PageviewTracker>
      <HelmetWrapper title="Home" />
      <MainLayout
        aside={
          userHasValidAccess &&
          hasCompany && (
            <>
              {isInnovator && <TechnologiesGridTile />}
              <ReferralTile />
            </>
          )
        }
        columnsStartAtContainer="68rem"
      >
        <div className="w-full flex flex-col gap-4">
          <HomeOnboardingTile />

          {isInnovator && userHasValidAccess && hasCompany && (
            <>
              <CompanyDetailsTile
                action="redirect"
                company={company}
                showDetails={false}
                showEmptyState={false}
              />
              {draftSurveys.length > 0 && <HomeTechnologyDraftSurveys draftSurveys={draftSurveys} />}
              <TechnologiesTrlSpectrumTile
                header={{ title: 'Technology Pipeline' }}
                technologies={technologies}
              />
            </>
          )}
        </div>
      </MainLayout>
    </PageviewTracker>
  );
};
